import { useState } from 'react';
import { Link } from 'react-router-dom';

const PaperPortfolioPlatformPage = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  
  // How it works cards
  const howItWorksCards = [
    {
      title: 'Start with $1,000 Virtual Cash',
      description: 'You get $1,000 in simulated funds to build your practice portfolio with zero real risk.'
    },
    {
      title: 'Buy & Sell Real Tickers',
      description: 'Practice with 15 real-world assets — ETFs, stocks, bonds — and see how your choices play out.'
    },
    {
      title: 'Analyze & Improve',
      description: 'Use the AI Portfolio Analyzer to get feedback on your strategy and learn what a well-balanced portfolio looks like.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0d0d0d', fontFamily: "'Inter', sans-serif" }}>
      {/* Google Fonts */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" 
        rel="stylesheet" 
      />

      {/* Sticky Nav */}
      <nav 
        className="sticky top-0 z-50 px-6 py-4 flex items-center justify-between"
        style={{ backgroundColor: '#111111' }}
      >
        <span 
          className="text-xl font-bold tracking-wide"
          style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}
        >
          POWR
        </span>
        <Link 
          to="/courses"
          className="text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: '#c9a84c' }}
        >
          ← Back to Course Hub
        </Link>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-20 text-center max-w-4xl mx-auto">
        <p 
          className="text-xs tracking-[0.2em] uppercase mb-6"
          style={{ color: '#c9a84c' }}
        >
          POWR COMMUNITY · INVESTMENT TOOLS
        </p>
        <h1 
          className="text-4xl md:text-5xl lg:text-6xl mb-6"
          style={{ 
            color: '#ffffff', 
            fontFamily: "'Playfair Display', serif",
            fontWeight: 500,
            lineHeight: 1.2
          }}
        >
          Paper Portfolio
        </h1>
        <p 
          className="text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed"
          style={{ color: '#e5e5e5' }}
        >
          Practice building wealth before you risk a single real dollar. Simulate real investments, analyze your strategy, and learn by doing.
        </p>
        <a
          href="https://www.perplexity.ai/computer/a/powr-paper-portfolio-NtEK9kyPSGSPpytAtKnDYw"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block font-bold transition-all hover:scale-105"
          style={{ 
            backgroundColor: '#c9a84c',
            color: '#000000',
            fontWeight: 700,
            borderRadius: '4px',
            padding: '14px 32px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#e2c97e'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#c9a84c'}
        >
          Open the Paper Portfolio
        </a>
        <p 
          className="text-xs mt-4 opacity-70"
          style={{ color: '#e5e5e5' }}
        >
          100% simulated · No real money · Practice only
        </p>
      </section>

      {/* Simulated Badge Banner */}
      <section className="px-6 py-4">
        <div 
          className="max-w-5xl mx-auto px-6 py-4"
          style={{ 
            backgroundColor: '#1a1a1a',
            borderLeft: '4px solid #c9a84c'
          }}
        >
          <p style={{ color: '#e2c97e' }}>
            ⚠ SIMULATED TOOL — All portfolio values, prices, and returns shown in this tool are fictional and for educational practice only. This is not real investing.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section 
        className="py-16 px-6"
        style={{ backgroundColor: '#111111' }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {howItWorksCards.map((card, index) => (
              <div 
                key={index}
                className="p-6"
                style={{ 
                  backgroundColor: '#111111',
                  borderTop: '3px solid #c9a84c'
                }}
              >
                <h3 
                  className="text-lg font-bold mb-3"
                  style={{ color: '#ffffff' }}
                >
                  {card.title}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: '#e5e5e5' }}
                >
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Iframe Embed */}
      <section className="w-full" style={{ height: '100vh', padding: 0 }}>
        {!iframeLoaded && (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: '#0d0d0d' }}
          >
            <div className="text-center">
              <div 
                className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                style={{ borderColor: '#c9a84c', borderTopColor: 'transparent' }}
              />
              <p style={{ color: '#e5e5e5' }}>Loading Paper Portfolio...</p>
            </div>
          </div>
        )}
        <iframe
          src="https://www.perplexity.ai/computer/a/powr-paper-portfolio-NtEK9kyPSGSPpytAtKnDYw"
          title="POWR Paper Portfolio"
          className="w-full h-full border-0"
          style={{ 
            display: iframeLoaded ? 'block' : 'none',
            overflow: 'hidden'
          }}
          onLoad={() => setIframeLoaded(true)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </section>

      {/* About This Tool */}
      <section 
        className="py-16 px-6"
        style={{ backgroundColor: '#111111' }}
      >
        <div className="max-w-5xl mx-auto">
          <h2 
            className="text-2xl md:text-3xl font-bold text-center mb-12"
            style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}
          >
            Why Practice First
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p 
                className="text-base leading-relaxed"
                style={{ color: '#e5e5e5' }}
              >
                Stewardship requires wisdom. Before you commit real dollars, you need to understand how markets work, how to build a diversified portfolio, and how to manage risk. This tool gives you a consequence-free environment to develop that wisdom.
              </p>
            </div>
            <div>
              <blockquote 
                className="text-2xl md:text-3xl font-medium italic leading-snug"
                style={{ 
                  color: '#c9a84c',
                  fontFamily: "'Playfair Display', serif"
                }}
              >
                "The wise man builds his house on rock. Practice is how you find the rock."
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-6 text-center">
        <p 
          className="text-xs max-w-3xl mx-auto leading-relaxed"
          style={{ color: '#666666' }}
        >
          Paper Portfolio is a simulated educational tool. All prices, portfolio values, and returns are hypothetical and do not reflect real market conditions. This tool does not constitute financial advice and is not a brokerage or investment service.
        </p>
      </section>

      {/* Footer */}
      <footer 
        className="py-12 px-6 text-center"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        <p 
          className="text-2xl font-bold mb-3"
          style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}
        >
          POWR
        </p>
        <p 
          className="text-sm mb-4"
          style={{ color: '#e5e5e5' }}
        >
          Purpose · Wealth · Legacy
        </p>
        <p 
          className="text-xs"
          style={{ color: '#666666' }}
        >
          © {new Date().getFullYear()} POWR. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default PaperPortfolioPlatformPage;
